export type HouseholdHit = { householdId: string; name: string; members: string }

export type HouseholdDancer = {
  dancerId: string
  firstName: string
  lastName: string
  dateOfBirth: string
  classes: number
}

export type HouseholdGuardian = {
  guardianId: string
  firstName: string
  lastName: string
  email: string | null
  mobilePhone: string | null
  relationship: string | null
  /**
   * Whether a login is attached (D-631). The supervisory read is keyed on the
   * account, so a guardian with none has nothing to read.
   */
  hasAccount: boolean

  /**
   * Whether she may sign in with her address, or holds it as contact detail only
   * because another guardian has that login (D-834).
   *
   * Not the same question as `hasAccount`: that one asks whether an account
   * exists, this asks whether one *could*. One login per address is the
   * database's own rule - the unique index is filtered to `CanSignIn = 1` - so a
   * shared address is ordinary and only the login is scarce.
   */
  canSignIn: boolean
}

export type HouseholdView = {
  householdId: string
  name: string
  addressLine1: string | null
  addressLine2: string | null
  city: string | null
  state: string | null
  postalCode: string | null
  dancers: HouseholdDancer[]
  guardians: HouseholdGuardian[]
}

/**
 * Families, filtered by a search term or all of them.
 *
 * **An empty box means everybody** (D-127). There is no minimum length any
 * more: the old two-character rule existed to stop one letter returning the
 * whole studio, and the whole studio is now what the page opens on.
 */
export async function searchHouseholds(q: string): Promise<HouseholdHit[]> {
  const term = q.trim()
  const query = term ? `?q=${encodeURIComponent(term)}` : ''

  const res = await fetch(`/api/admin/people${query}`)
  if (!res.ok) throw new Error(`Could not load the families (${res.status})`)

  return res.json()
}

/** What stands between a guardian and a login (D-155). */
export type GuardianInviteState = {
  guardianId: string
  name: string
  email: string | null
  /** None, NoEmail, AlreadyRegistered, AlreadyInvited, NotASignIn. */
  skip: string
  invitedAt: string | null
  acceptedAt: string | null
}

export async function fetchInviteState(guardianId: string): Promise<GuardianInviteState> {
  const res = await fetch(`/api/admin/people/${guardianId}/invite`)
  if (!res.ok) throw new Error(`Could not check that (${res.status})`)
  return res.json()
}

/**
 * Sends one invitation to one family.
 *
 * <b>One at a time on purpose.</b> There is no bulk send — a new sending domain
 * blasting 163 cold messages is the worst possible way to warm it, and families
 * mostly arrive through the public claim page. This is for starting a pilot, and
 * for the parent who rings up saying it never worked (D-155).
 */
export async function inviteGuardian(
  guardianId: string,
): Promise<{ email: string; sent: boolean; problem: string | null }> {
  const res = await fetch(`/api/admin/people/${guardianId}/invite`, { method: 'POST' })

  if (!res.ok) {
    const problem = await res.json().catch(() => null)
    throw new Error(problem?.detail ?? problem?.title ?? `Could not send that (${res.status})`)
  }

  return res.json()
}

export async function fetchHousehold(id: string): Promise<HouseholdView> {
  const res = await fetch(`/api/admin/people/${id}`)
  if (!res.ok) throw new Error(`Could not load that family (${res.status})`)

  return res.json()
}

async function put<T>(url: string, body: unknown): Promise<T> {
  const res = await fetch(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })

  if (!res.ok) {
    const problem = await res.json().catch(() => null)
    throw new Error(problem?.error ?? `Could not save that (${res.status})`)
  }

  return res.json()
}

export const saveHousehold = (id: string, edit: Omit<HouseholdView, 'householdId' | 'dancers' | 'guardians'>) =>
  put<HouseholdView>(`/api/admin/people/${id}`, edit)

export const saveDancer = (
  id: string,
  edit: { firstName: string; lastName: string; dateOfBirth: string; notes: string | null },
) => put<HouseholdView>(`/api/admin/people/dancers/${id}`, edit)

export const saveGuardian = (
  id: string,
  edit: {
    firstName: string
    lastName: string
    email: string | null
    mobilePhone: string | null
    relationship: string | null
  },
) => put<HouseholdView>(`/api/admin/people/guardians/${id}`, edit)

/**
 * Why a date of birth looks wrong, or null.
 *
 * Mirrors the server. **Not a cosmetic check** — age eligibility is computed
 * from this against each class's range (D-50), so a wrong digit quietly moves a
 * child into or out of a class.
 */
export function dateOfBirthProblem(value: string, now: Date = new Date()): string | null {
  if (!value) return 'A date of birth is needed — it decides class eligibility.'

  const parsed = new Date(`${value}T00:00:00`)
  if (Number.isNaN(parsed.getTime())) return 'That is not a date.'

  if (parsed > now) return 'That is in the future.'
  if (now.getFullYear() - parsed.getFullYear() > 100) return 'That is over a hundred years ago.'

  return null
}

/** "8" — shown beside a birthday so a wrong year is obvious at a glance. */
export function ageOn(dateOfBirth: string, on: Date = new Date()): number | null {
  const born = new Date(`${dateOfBirth}T00:00:00`)
  if (Number.isNaN(born.getTime())) return null

  let age = on.getFullYear() - born.getFullYear()
  const month = on.getMonth() - born.getMonth()

  if (month < 0 || (month === 0 && on.getDate() < born.getDate())) age -= 1

  return age
}
